import axios, { CancelTokenSource } from "axios";

export interface SearchResult<T> {
	results: T[];
	totalCount: number;
	isValidScriptureSearch: boolean;
	isChapter: boolean;
}

export interface SermonSearchItem {
	id: string;
	title: string;
	contributor: string;
	description: string;
	rating: number;
}

export interface IllustrationSearchItem {
	id: string;
	title: string;
	contributor: string;
	description: string;
	rating: number;
}

export interface BibleVerseSearchItem {
	title: string;
	scriptureReference: string;
	description: string;
}

export interface SermonContentItem {
	id: number;
	title: string;
	summary: string;
	authorFirstName: string;
	authorLastName: string;
	body: string;
}

export interface IllustrationContentItem {
	id: string;
	title: string;
	authorFirstName: string;
	authorLastName: string;
	body: string;
}

export interface BibleChapterContentItem {
	bookName: string;
	chapterNumber: number;
	translationName: string;
	translationId: string;
	verses: BibleVerseContentItem[];
}

export interface BibleVerseContentItem {
	verseNumberStart: number;
	verseNumberEnd: number;
	verseContent: string;
	startsParagraph: boolean;
}

export class SearchServiceHelper {
	static removeTranslationNotes(verse: string) {
		const translationNoteRegexSurroundingBrackets = /\[T.N.[^\]]*\]/g;
		const translationNoteRegexSurroundingParenths = /\(T.N.[^)]*\)/g;
		const translationNoteRegex = /, T.N.[^)]*\)/g;
		const translationNoteRegexLeadingSemicolonEndingBracket = /; T.N.[^\]]*\]/g;
		const translationNoteRegexNoComma = /T.N.[^)]*\)/g;
		const translationNoteRegexEndingBracket = /T.N.[^\]]*\]/g;
		const translationNoteRegexEmptyNote = /T.N./g;
		const footnoteRegex = /<footnote>.*<\/footnote>/g;

		if (verse.match(translationNoteRegexSurroundingBrackets)) {
			verse = verse.replace(translationNoteRegexSurroundingBrackets, "");
		}
		if (verse.match(translationNoteRegexSurroundingParenths)) {
			verse = verse.replace(translationNoteRegexSurroundingParenths, "");
		}
		if (verse.match(translationNoteRegex)) {
			verse = verse.replace(translationNoteRegex, ")");
		}
		if (verse.match(translationNoteRegexLeadingSemicolonEndingBracket)) {
			verse = verse.replace(translationNoteRegexLeadingSemicolonEndingBracket, "]");
		}
		if (verse.match(translationNoteRegexNoComma)) {
			verse = verse.replace(translationNoteRegexNoComma, ")");
		}
		if (verse.match(translationNoteRegexEndingBracket)) {
			verse = verse.replace(translationNoteRegexEndingBracket, "]");
		}
		if (verse.match(translationNoteRegexEmptyNote)) {
			verse = verse.replace(translationNoteRegexEmptyNote, "");
		}
		if (verse.match(footnoteRegex)) {
			verse = this.removeFootNotes(verse);
		}
		return verse;
	}

	static removeVerseNumbers(verse: string) {
		const verseNumberRegex = /\(\d*\)/g;
		const verseNumberTagRegex = /<sup>\d*<\/sup> /g;
		if (verseNumberRegex.test(verse)) {
			verse = verse.replace(verseNumberRegex, "");
		}
		if (verseNumberTagRegex.test(verse)) {
			verse = verse.replace(verseNumberTagRegex, "");
		}
		return verse;
	}

	static removeFootNotes(verse:string){
		let retVal = "";
		let start = 0

		while(start < verse.length){
			let open = verse.indexOf("<footnote>", start);
			let close = verse.indexOf("</footnote>", open);

			if(close === -1 || open === -1){
				retVal += verse.slice(start);
				start = verse.length;
				break;
			}

			if(start === -1){
				start = verse.length;
				continue;
			}

			retVal += `${verse.slice(start, open)} `;
			start = close;	
		}
		return retVal;
	}

	static replace(regEx: RegExp, replacementData: string, data: string) {
		return data.replace(new RegExp(regEx, "g"), replacementData);
	}
}

export class SearchService {
	static async findSermons(
		queryText: string,
		preferredLanguageId: string = "english",
		skip: number = 0,
		take: number = 10,
		cancellationToken: CancelTokenSource
	): Promise<SearchResult<SermonSearchItem>> {
		let url = "/api/Search/FindSermons";
		url += `?q=${queryText}`;
		url += `&skip=${skip}`;
		url += `&take=${take}`;

		try {
			const result = await axios.get(url, { cancelToken: cancellationToken.token});

			const searchResponse: SearchResult<SermonSearchItem> = result.data.data;

			return searchResponse;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	static async findIllustrations(
		queryText: string,
		skip: number = 0,
		take: number = 10,
		cancellationToken: CancelTokenSource
	): Promise<SearchResult<IllustrationSearchItem>> {
		let url = "/api/Search/FindIllustrations";
		url += `?q=${queryText}`;
		url += `&skip=${skip}`;
		url += `&take=${take}`;

		try {
			const result = await axios.get(url, { cancelToken: cancellationToken.token});

			const searchResponse: SearchResult<IllustrationSearchItem> = result.data.data;

			return searchResponse;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	static async findBibleVerses(
		queryText: string,
		translationId: string = "NIV",
		skip: number = 0,
		take: number = 10,
		cancellationToken: CancelTokenSource
	): Promise<SearchResult<BibleVerseSearchItem>> {
		let url = "/api/Search/FindBibleVerses";
		url += `?q=${queryText}`;
		url += `&skip=${skip}`;
		url += `&take=${take}`;
		url += `&translationId=${translationId}`;

		try {
			const result = await axios.get(url, { cancelToken: cancellationToken.token});

			const searchResponse: SearchResult<BibleVerseSearchItem> = result.data.data;

			return searchResponse;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	static async getSermon(id: string): Promise<SermonContentItem> {
		let url = `/api/Search/GetSermon/${id}`;

		try {
			const result = await axios.get(url);
			const response: SermonContentItem = result.data.data;
			return response;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	static async getIllustration(id: string): Promise<IllustrationContentItem> {
		let url = `/api/Search/GetIllustration/${id}`;

		try {
			const result = await axios.get(url);
			const response: IllustrationContentItem = result.data.data;
			return response;
		} catch (error) {
			return Promise.reject(error);
		}
	}

	static async getBibleChapter(scriptureReference: string, translationId: string): Promise<BibleChapterContentItem> {
		let url = "/api/Search/GetBibleChapter";
		url += `?scriptureReference=${scriptureReference}`;
		url += `&translationId=${translationId}`;

		try {
			const result = await axios.get(url);
			let response: BibleChapterContentItem = result.data.data;

			return response;
		} catch (error) {
			return Promise.reject(error);
		}
	}
}
